export type DatePresetKey = 'this-week' | 'this-month' | 'three-months' | 'closing-soon';

export interface DatePreset {
  key: DatePresetKey;
  label: string;
  sub: string;
}

export const DATE_PRESETS: DatePreset[] = [
  { key: 'this-week', label: '이번 주', sub: '~ 일요일' },
  { key: 'this-month', label: '이번 달', sub: '월말까지' },
  { key: 'three-months', label: '3개월 이내', sub: '90일' },
  { key: 'closing-soon', label: '접수 임박', sub: 'D-7 이내' },
];

export function stripTime(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function parseDate(s: string | undefined | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

type KorDateFormat = 'month-day' | 'full' | 'numeric' | 'numeric-weekday';

const KOR_TZ = 'Asia/Seoul';

const KOR_DATE_OPTIONS: Record<KorDateFormat, Intl.DateTimeFormatOptions> = {
  'month-day': { month: 'long', day: 'numeric', timeZone: KOR_TZ },
  full: { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short', timeZone: KOR_TZ },
  numeric: { month: 'numeric', day: 'numeric', timeZone: KOR_TZ },
  'numeric-weekday': { month: 'numeric', day: 'numeric', weekday: 'short', timeZone: KOR_TZ },
};

export function formatKorDate(
  dateStr: string | undefined | null,
  format: KorDateFormat = 'month-day',
  fallback: string = '—',
): string {
  const d = parseDate(dateStr);
  if (!d) return fallback;
  return d.toLocaleDateString('ko-KR', KOR_DATE_OPTIONS[format]);
}

function toISO(d: Date): string {
  return d.toISOString();
}

export function getDateRangeForPreset(
  key: DatePresetKey,
  now: Date = new Date()
): { startDate: string; endDate: string } {
  const today = stripTime(now);

  if (key === 'this-week') {
    const day = today.getDay();
    const daysUntilSunday = (7 - day) % 7;
    const end = new Date(today);
    end.setDate(end.getDate() + daysUntilSunday);
    end.setHours(23, 59, 59, 999);
    return { startDate: toISO(today), endDate: toISO(end) };
  }

  if (key === 'this-month') {
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
    return { startDate: toISO(today), endDate: toISO(end) };
  }

  if (key === 'three-months') {
    const end = new Date(today);
    end.setMonth(end.getMonth() + 3);
    end.setHours(23, 59, 59, 999);
    return { startDate: toISO(today), endDate: toISO(end) };
  }

  // closing-soon: D-7 window
  const end = new Date(today);
  end.setDate(end.getDate() + 7);
  end.setHours(23, 59, 59, 999);
  return { startDate: toISO(today), endDate: toISO(end) };
}

export function getActiveDatePreset(
  startDate: string | undefined,
  endDate: string | undefined,
  now: Date = new Date()
): DatePresetKey | null {
  if (!startDate || !endDate) return null;

  const inputStart = new Date(startDate).getTime();
  const inputEnd = new Date(endDate).getTime();

  for (const preset of DATE_PRESETS) {
    const range = getDateRangeForPreset(preset.key, now);
    const presetStart = new Date(range.startDate).getTime();
    const presetEnd = new Date(range.endDate).getTime();
    if (
      Math.abs(inputStart - presetStart) < 60_000 &&
      Math.abs(inputEnd - presetEnd) < 60_000
    ) {
      return preset.key;
    }
  }
  return null;
}

export function formatDateRangeLabel(
  startDate: string | undefined,
  endDate: string | undefined,
  now: Date = new Date()
): string {
  const preset = getActiveDatePreset(startDate, endDate, now);
  if (preset) {
    return DATE_PRESETS.find((p) => p.key === preset)?.label ?? '기간';
  }
  return '기간 지정';
}
